import { useMemo, useState, useEffect, useCallback, useReducer } from 'react';

import useSectionSearchColumns from './useSectionSearchColumns';

import { useGetAdvancedSectionSearch } from 'hooks';

import { showErrorToast } from 'utils/showToast';
import {
  sectionColumnData,
  searchItem,
  sortItem,
  sectionInitialState,
} from 'utils/advanceSearchUtils';

import {
  QuestionSearchParams,
  SearchRuleItem,
  SectionOptionsType,
} from '../../advancedSearchTypes';

import useAdvancedSearchStore from 'store/useAdvancedSearchStore';

function reducer(
  state: SectionOptionsType,
  action: {
    type: keyof SectionOptionsType | 'reset';
    newState?: SectionOptionsType;
  },
) {
  switch (action.type) {
    case 'includeGlobal': {
      return {
        ...state,
        includeGlobal: !state.includeGlobal,
      };
    }
    case 'includeInactive': {
      return {
        ...state,
        includeInactive: !state.includeInactive,
      };
    }
    case 'includeHidden': {
      return {
        ...state,
        includeHidden: !state.includeHidden,
      };
    }
    case 'reset':
      if (action.newState) return { ...action.newState };
      return { ...sectionInitialState };
    default:
      throw new Error();
  }
}
const initialSearchList: Array<SearchRuleItem> = [{ ...searchItem }];

export default function useSectionSearch() {
  // search block states
  const [searchList, setSearchList] = useState<Array<SearchRuleItem>>([
    ...initialSearchList.map(item => item),
  ]);
  const [searchParams, setSearchParams] = useState<QuestionSearchParams>();
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);

  //checkbox states
  const [sectionOptions, setSectionOptions] = useReducer(reducer, {
    ...sectionInitialState,
  });

  const { searchStore, setParams } = useAdvancedSearchStore();

  useEffect(() => {
    if (searchStore) {
      if (searchStore.sectionSearchParams) {
        const { includeGlobal, includeInactive, searchList, includeHidden } =
          searchStore.sectionSearchParams;
        setSectionOptions({
          type: 'reset',
          newState: {
            includeGlobal,
            includeInactive,
            includeHidden,
          },
        });
        setSearchList(searchList);
      }
      if (searchStore.sortItem) {
        setCurrentSortItem(searchStore.sortItem);
      }
      if (searchStore.columnSearch) {
        setColumnsSearch(searchStore.columnSearch);
      }
      setSearchParams(searchStore.sectionSearchParams);
    }
  }, [searchStore]);

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: advancedSearchloading,
  } = useGetAdvancedSectionSearch(searchParams, columnsSearch, currentSortItem);

  const tableData = useMemo(() => {
    if (data) {
      return data.pages
        .map((dataItem: any) => dataItem?.Data?.Data)
        .flat()
        .filter((item: any) => !!item);
    }
    return;
  }, [data]);

  const csvImportData = tableData?.length ? tableData : [];
  const sectionsDataCsv = [
    [
      'AccountId',
      'CID',
      'PID',
      'SID',
      'AccountName',
      'FormId',
      'FormStatus',
      'FormNotes',
      'SectionTitle',
      'SectionCallType',
    ],
    ...csvImportData.map(
      ({
        AccountId,
        CID,
        PID,
        SID,
        AccountName,
        FormId,
        FormStatus,
        FormNotes,
        SectionTitle,
        SectionCallType,
      }: any) => [
        AccountId,
        CID,
        PID,
        SID,
        AccountName,
        FormId,
        FormStatus,
        FormNotes,
        SectionTitle,
        SectionCallType,
      ],
    ),
  ];

  const onEndReached = () => {
    if (hasNextPage) fetchNextPage();
  };

  const columnList = useSectionSearchColumns([...sectionColumnData.slice()]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const resetSectionSearch = () => {
    setSearchList([{ ...searchItem }]);
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    setSectionOptions({ type: 'reset' });
    setSearchParams(undefined);
  };

  const toggleSectionOption = (key?: keyof SectionOptionsType) => () => {
    if (!key) return setSectionOptions({ type: 'reset' });
    return setSectionOptions({ type: key });
  };

  function searchButtonClick() {
    if (searchList.filter(item => item.value.trim().length === 0).length > 0) {
      showErrorToast({
        message: 'Please Enter a keyword',
      });
    } else {
      setSearchParams({
        searchList: JSON.parse(JSON.stringify(searchList.slice())),
        ...sectionOptions,
      });
    }
  }

  function onClickToolTip(
    path: string,
    params: Record<string, number | string>,
  ) {
    setParams({
      sectionSearchParams: searchParams,
      columnSearch: columnsSearch,
      sortItem: currentSortItem,
    });
    localStorage.setItem('fromSearch', JSON.stringify(params?.Pid));
    window.open(path);
  }

  return {
    sectionsDataCsv,
    sectionOptions,
    toggleSectionOption,
    resetSectionSearch,
    sectionTableProps: {
      columns: columnList,
      columnsSearch: columnsSearch,
      data: tableData,
      headerUpdateCallBack: headerUpdateCallBack,
      sortedItem: currentSortItem,
      handleSortChange: handleSortChange,
      isLoading: advancedSearchloading,
      noOptions: true,
      onEndReached: onEndReached,
      isFetching: !!isFetchingNextPage,
      onRedirect: onClickToolTip,
    },
    sectionSearch: {
      searchList,
      setSearchList,
      searchButtonClick,
    },
  };
}
